import { fetchEtherscan } from './etherscan';
import { fetchPolygonscan } from './polygonscan';
import { fetchBscscan } from './bscscan';
import { fetchTon } from './ton';
import { fetchTonTestnet } from './ton-testnet';

const MAX_RETRIES   = 4;
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS  = 15000;

function sleep(ms) {
    return new Promise(r => setTimeout(r, ms));
}

/**
 * Wrap an async function with retry + exponential backoff.
 * Adapters return [] on HTTP errors (including 429), so we
 * do a confirmation re-fetch when the first call returns []
 * to distinguish "genuinely empty" from "rate-limited".
 */
async function withRetry(fn) {
    let lastErr;

    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
            const result = await fn();

            if (Array.isArray(result) && result.length > 0) {
                return result;
            }

            // First empty → one confirmation attempt after a short pause
            if (attempt === 0 && Array.isArray(result) && result.length === 0) {
                await sleep(BASE_DELAY_MS);
                const second = await fn();
                if (Array.isArray(second) && second.length > 0) {
                    return second;
                }
                // Both empty → genuinely no data
                return [];
            }

            lastErr = new Error('Empty response, possible rate limit');
        } catch (e) {
            lastErr = e;
        }

        if (attempt < MAX_RETRIES) {
            const delay = Math.min(BASE_DELAY_MS * 2 ** attempt, MAX_DELAY_MS);
            const jitter = delay * (0.5 + Math.random() * 0.5);
            await sleep(jitter);
        }
    }

    console.error('withRetry exhausted:', lastErr);
    return [];
}

export async function fetchTransactionsForAddress(address, network) {
    return withRetry(() => {
        switch (network) {
        case 'Ethereum':
            return fetchEtherscan(address);
        case 'Polygon':
            return fetchPolygonscan(address);
        case 'BSC':
            return fetchBscscan(address);
        case 'TON':
            return fetchTon(address);
        case 'TON Testnet':
            return fetchTonTestnet(address);
        default:
            return Promise.resolve([]);
        }
    });
}